import {
  DerivedAvailabilityStatus,
  DerivedAvailabilityStatusInternal,
  productDataTransformer,
  type Bundle,
  type IProduct,
  type ProductData,
  type Slot,
} from '@commerce-product-webcomponents/sdk';
import { SlotType, checkPriorityBundleStatus, type IBundleState } from '@commerce-product-webcomponents/ui';
import { SectionIds } from './types/enums';
import type { BundleSlotType, DynamicBundleValuesType } from './types/propsTypes';

export const controlCollection = [
  { title: 'product.pdpNavigator.overview', id: SectionIds.OVERVIEW, display: true },
  { title: 'product.productDetails.header', id: SectionIds.PRODUCT_DETAILS, display: true },
  { title: 'product.productIngredient.ingredientMainTitle', id: SectionIds.INGREDIENTS, display: true },
  { title: 'product.usageComponent.header', id: SectionIds.HOW_TO_USE, display: true },
  { title: 'product.faqs.heading', id: SectionIds.FAQS, display: true },
];

const dynamicInitialValues = {
  productPrice: 0,
  retailPrice: 0,
  pointValue: 0,
  buisnessVolume: 0,
};

export const getUpdatedStateNavigationTabs = (productData: IProduct) =>
  controlCollection.map((item) => {
    const { faqs, instructions, productDetail, ingredientsData: { ingredientsDetail = {} } = {} } = productData;
    const { ingredSectionText = '', supplementFact = '' } = ingredientsDetail;
    switch (item.id) {
      case SectionIds.PRODUCT_DETAILS:
        item.display = productDetail && Object.keys(productDetail).length !== 0;
        break;
      case SectionIds.INGREDIENTS:
        item.display =
          ingredientsDetail &&
          Object.keys(ingredientsDetail).length !== 0 &&
          (ingredSectionText !== '' || supplementFact !== '');
        break;
      case SectionIds.HOW_TO_USE:
        item.display = instructions ? instructions?.length !== 0 : false;
        break;
      case SectionIds.FAQS:
        item.display = faqs && faqs?.length !== 0;
        break;
      default:
        break;
    }
    return item;
  });

export const calculateBundlePrice = (bundle: Bundle | undefined, isBundle: boolean): DynamicBundleValuesType => {
  const dynamicValues = { ...dynamicInitialValues };
  if (isBundle) {
    const slots = bundle?.slots || [];
    slots.forEach((slot: Slot) => {
      const obj =
        slot?.products?.reduce((acc, product: ProductData) => {
          const { productPrice, productRetailPrice, businessVolume, pointValue } = productDataTransformer(product);
          return {
            productPrice: acc.productPrice + productPrice * (slot.minQty || 0),
            retailPrice: acc.retailPrice + Number(productRetailPrice || 0) * (slot.minQty || 0),
            pointValue: acc.pointValue + Number(pointValue) * (slot.minQty || 0),
            buisnessVolume: acc.buisnessVolume + Number(businessVolume) * (slot.minQty || 0),
          };
        }, dynamicInitialValues) || dynamicInitialValues;
      dynamicValues.productPrice += obj.productPrice;
      dynamicValues.retailPrice += obj.retailPrice;
      dynamicValues.buisnessVolume += obj.buisnessVolume;
      dynamicValues.pointValue += obj.pointValue;
    });
    return dynamicValues;
  } else {
    return dynamicInitialValues;
  }
};

const checkForPartialBO = (bundleState: IBundleState[], minPoolQty: number): DerivedAvailabilityStatusInternal => {
  let bundleStatus = '';
  const fixedAndVariantProductLength = bundleState.filter((state) =>
    [SlotType.FIXED as string, SlotType.VARIANT as string].includes(state?.slotType as string),
  ).length;
  const mandatoryLength = fixedAndVariantProductLength + minPoolQty;
  const boProductQtySelected = bundleState.reduce((acc: number, curValue: IBundleState): number => {
    if (curValue.deriveStockStatus === DerivedAvailabilityStatusInternal.Bo && curValue.quantity > 0) {
      return acc + curValue.quantity;
    }
    return acc;
  }, 0);
  const otherStatusSelectedForPoolLength = bundleState.filter(
    (state) =>
      state.quantity > 0 &&
      state.slotType === SlotType.POOL &&
      [DerivedAvailabilityStatusInternal.InStock, DerivedAvailabilityStatusInternal.LowStock].includes(
        state.deriveStockStatus as DerivedAvailabilityStatusInternal,
      ),
  ).length;
  if (mandatoryLength <= boProductQtySelected && otherStatusSelectedForPoolLength === 0) {
    bundleStatus = DerivedAvailabilityStatusInternal.Bo;
  } else {
    bundleStatus = DerivedAvailabilityStatusInternal.Pbo;
  }
  return bundleStatus as DerivedAvailabilityStatusInternal;
};

export const calculateBundleStatus = (
  bundleState: IBundleState[],
  minPoolQty: number,
  bundle: Bundle | undefined,
): DerivedAvailabilityStatusInternal => {
  const bundleStatusArray = bundleState.filter((state) => state.quantity !== 0).map((state) => state.deriveStockStatus);
  const { derivedAvailabilityStatus } = bundle || {};
  const prioritizedStatus =
    derivedAvailabilityStatus === DerivedAvailabilityStatus.Nya
      ? (derivedAvailabilityStatus as DerivedAvailabilityStatusInternal)
      : checkPriorityBundleStatus(bundleStatusArray as DerivedAvailabilityStatusInternal[]);
  if (prioritizedStatus === DerivedAvailabilityStatusInternal.Bo) {
    return checkForPartialBO(bundleState, minPoolQty);
  }
  return prioritizedStatus;
};

export const calculateLatestAvailableFutureDate = (bundleState: IBundleState[], isBundle: boolean): string => {
  let availableFutureDate = '';
  if (isBundle) {
    const dates = bundleState
      ?.reduce((acc: string[], { quantity, firstAvailableFutureDate: date }) => {
        if (quantity !== 0 && date) {
          acc.push(date);
        }
        return acc;
      }, [])
      ?.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const length = dates?.length;
    availableFutureDate = length > 0 ? dates[length - 1] : '';
  }
  return availableFutureDate;
};

export const onBundleProductSelected = (
  bundleState: IBundleState[],
  isBundleConditionMatched: { value: boolean },
  enableAddToCart: boolean,
  minPoolQty: number,
  isBundleUpdated: { value: boolean },
  bundle: Bundle | undefined,
  isBundle: boolean,
  setIsBundleConditionMatched: React.Dispatch<React.SetStateAction<{ value: boolean }>>,
  setShowToast: React.Dispatch<React.SetStateAction<boolean>>,
  setDynamicBundleValues: React.Dispatch<React.SetStateAction<DynamicBundleValuesType>>,
  dynamicBundleValues: DynamicBundleValuesType,
  setUpdatedBundleState: React.Dispatch<React.SetStateAction<IBundleState[]>>,
  setBundleSlots: React.Dispatch<React.SetStateAction<BundleSlotType[]>>,
  setIsCartDisabled: React.Dispatch<React.SetStateAction<boolean>>,
  setNewBundleDerivedStatus: React.Dispatch<React.SetStateAction<DerivedAvailabilityStatusInternal>>,
  setNewFirstAvailableFutureDate: React.Dispatch<React.SetStateAction<string>>,
  showToast = true,
) => {
  setShowToast(showToast);
  setIsBundleConditionMatched(isBundleConditionMatched);
  setDynamicBundleValues(
    bundleState?.reduce((acc, product: IBundleState) => {
      const { price, quantity, retailPrice, pointValue, buisnessVolume } = product;
      return {
        productPrice: acc.productPrice + price * quantity,
        retailPrice: acc.retailPrice + Number(retailPrice) * quantity,
        pointValue: acc.pointValue + Number(pointValue) * quantity,
        buisnessVolume: acc.buisnessVolume + Number(buisnessVolume) * quantity,
      };
    }, dynamicInitialValues) || dynamicBundleValues,
  );

  setUpdatedBundleState(bundleState);
  setBundleSlots(
    bundleState.reduce((newBundleSlots: BundleSlotType[], bundleState: IBundleState) => {
      if (bundleState.quantity > 0) {
        const index = newBundleSlots.findIndex(
          (newBundleSlot: BundleSlotType) => newBundleSlot.id === bundleState.slotId.toString(),
        );
        if (newBundleSlots[index]) {
          newBundleSlots[index].entries.push({
            product: { id: bundleState.productId },
            quantity: bundleState.quantity,
          });
        } else {
          const bundleSlotObj: BundleSlotType = {
            id: bundleState.slotId.toString(),
            entries: [
              {
                product: { id: bundleState.productId },
                quantity: bundleState.quantity,
              },
            ],
          };
          newBundleSlots.push(bundleSlotObj);
        }
      }
      return newBundleSlots;
    }, []),
  );

  setIsCartDisabled(!enableAddToCart);
  // Logic for calculation of bundleDerivedStatus
  if (isBundleUpdated.value) {
    const finalDerivedBundleStatus = calculateBundleStatus(bundleState, minPoolQty, bundle);
    setNewBundleDerivedStatus(finalDerivedBundleStatus);
    const firstAvailableFutureDate = calculateLatestAvailableFutureDate(bundleState, isBundle);
    setNewFirstAvailableFutureDate(firstAvailableFutureDate);
  }
};

export const getRespectivePrice = (
  price: number | string,
  bundle: Bundle | undefined,
  isBundleConditionMatched: { value: boolean },
  finalProdutPrice: number,
): number | string => {
  const { slots = [] } = bundle || ({} as Bundle);
  const isPool = slots.find((s: Slot) => s.type === SlotType.POOL);
  if (isPool) {
    return isBundleConditionMatched?.value ? finalProdutPrice : price || finalProdutPrice;
  } else {
    return finalProdutPrice || price;
  }
};
